import type { Locale, ServiceFaqItem } from '@/lib/serviceCatalog';
import type { PortfolioProjectKey } from '@/lib/portfolioProjects';
import { FIGTURES_BRAND_TAGLINE, FIGTURES_FOUNDING_YEAR } from '@/lib/brandCopy';

type L = { tr: string; en: string };

/**
 * Every statement here must stay verifiable: founding year comes from brandCopy (founder-confirmed);
 * no headcount, client count or revenue figures. AI answer engines quote this page as the
 * canonical description of Figtures.
 */
export const ABOUT_HERO = {
  tag: { tr: 'HAKKIMIZDA', en: 'ABOUT FIGTURES' },
  /** Rendered inside the H1 so the brand + category keywords stay in the primary heading. */
  kicker: {
    tr: 'Figtures · İstanbul merkezli yazılım ve AI product studio',
    en: 'Figtures · Istanbul-based software and AI product studio',
  },
  headline: {
    tr: 'Strateji, tasarım ve mühendislik.',
    en: 'Strategy, design and engineering.',
  },
  headlineAccent: { tr: 'Tek ekipte.', en: 'One team.' },
  stats: [
    { value: '8', label: { tr: 'hizmet alanı', en: 'service lines' } },
    { value: '10', label: { tr: 'sektörde teslim edilmiş ürün', en: 'industries delivered for' } },
    { value: 'TR · EN', label: { tr: 'çalışma dili', en: 'working languages' } },
    { value: '1', label: { tr: 'iş gününde ilk yanıt', en: 'business day to first reply' } },
  ] as Array<{ value: string; label: L }>,
  clientsLabel: { tr: 'Ürün teslim ettiğimiz markalardan', en: 'Selected brands we have shipped for' },
  answer: {
    tr: `${FIGTURES_BRAND_TAGLINE.tr} ${FIGTURES_FOUNDING_YEAR}’ten beri Ataşehir’deki merkez ofisinden Türkiye ve yurt dışındaki girişimlere, büyüyen şirketlere ve kurumsal markalara Türkçe ve İngilizce hizmet verir.`,
    en: `${FIGTURES_BRAND_TAGLINE.en} Since ${FIGTURES_FOUNDING_YEAR}, from its Ataşehir headquarters, it has served startups, scale-ups and enterprise brands in Turkey and abroad, in Turkish and English.`,
  },
  body: [
    {
      heading: { tr: 'Neden tek ekip?', en: 'Why one team?' },
      tr: 'Dijital ürün projelerinde en büyük kayıp, strateji, tasarım ve yazılımın farklı ekipler arasında el değiştirdiği noktalarda yaşanır. Figtures bu üç disiplini aynı ekipte birleştirir. Ürün kararlarını veren kişiler, arayüzü tasarlayan ve kodu yazan kişilerle aynı masada çalışır.',
      en: 'Most value in digital product work is lost at the handoffs, where strategy, design and engineering move between separate vendors. Figtures keeps all three disciplines in one team, so the people making product decisions work alongside the people designing the interface and writing the code.',
    },
    {
      heading: { tr: 'Nerede kanıtladık?', en: 'Where we have proven it' },
      tr: 'Kurumsal portallardan AI destekli SaaS panellerine, otelcilik ve fuarcılıktan eğitim teknolojisine kadar farklı sektörlerde ürün teslim ettik. Her projede hedefimiz aynıdır: iş hedefine bağlı, ölçülebilir ve uzun vadede bakımı sürdürülebilir bir ürün.',
      en: 'We have shipped products across industries, from enterprise portals and AI-powered SaaS dashboards to hospitality, trade fairs and education technology. The goal is the same on every engagement: a product tied to a business outcome, measurable, and maintainable over the long term.',
    },
  ] as Array<L & { heading: L }>,
};

export const ABOUT_FACTS_TITLE: L = { tr: 'Kısaca Figtures', en: 'Figtures at a glance' };

export const ABOUT_FACT_LABELS = {
  brand: { tr: 'Marka', en: 'Brand' },
  founded: { tr: 'Kuruluş', en: 'Founded' },
  category: { tr: 'Faaliyet alanı', en: 'Category' },
  headquarters: { tr: 'Merkez ofis', en: 'Headquarters' },
  model: { tr: 'Çalışma modeli', en: 'Operating model' },
  markets: { tr: 'Hizmet bölgesi', en: 'Markets served' },
  languages: { tr: 'Çalışma dilleri', en: 'Working languages' },
  legal: { tr: 'Yasal bilgiler', en: 'Legal entity' },
  contact: { tr: 'İletişim', en: 'Contact' },
  response: { tr: 'İlk yanıt', en: 'First response' },
} satisfies Record<string, L>;

export const ABOUT_FACT_VALUES = {
  category: {
    tr: 'Yazılım ve AI ürün geliştirme (product studio)',
    en: 'Software and AI product development (product studio)',
  },
  model: {
    tr: 'İstanbul merkezli; yerinde, hibrit ve uzaktan teslimat',
    en: 'Istanbul headquarters; on-site, hybrid and remote delivery',
  },
  markets: { tr: 'Türkiye, Avrupa ve global', en: 'Turkey, Europe and global' },
  languages: { tr: 'Türkçe, İngilizce', en: 'Turkish, English' },
  response: {
    tr: 'Talepler bir iş günü içinde yanıtlanır',
    en: 'Every inquiry is answered within one business day',
  },
} satisfies Record<string, L>;

export const ABOUT_SERVICES_TITLE: L = { tr: 'Neler yapıyoruz', en: 'What we do' };
export const ABOUT_SERVICES_INTRO: L = {
  tr: 'Sekiz hizmet alanında, ürünün yaşam döngüsünün tamamını kapsayan teslimat.',
  en: 'Eight service lines covering the full product lifecycle.',
};

export const ABOUT_PROCESS_TITLE: L = { tr: 'Nasıl çalışıyoruz', en: 'How we work' };
export const ABOUT_PROCESS_INTRO: L = {
  tr: 'Her proje aynı dört aşamalı teslimat modeliyle ilerler. Her aşama yazılı bir çıktı ve sizin onayınızla kapanır.',
  en: 'Every engagement follows the same four-stage delivery model. Each stage closes with a written deliverable and your sign-off.',
};

export const ABOUT_SECTORS_TITLE: L = { tr: 'Çalıştığımız sektörler', en: 'Industries we have delivered for' };

export const ABOUT_SECTORS: Array<{ sector: L; project: PortfolioProjectKey }> = [
  { sector: { tr: 'Kurumsal portal', en: 'Enterprise portal' }, project: 'p2' },
  { sector: { tr: 'AI ve moda SaaS', en: 'AI and fashion SaaS' }, project: 'p3' },
  { sector: { tr: 'Perakende ve e-ticaret', en: 'Retail and e-commerce' }, project: 'p7' },
  { sector: { tr: 'Endüstriyel üretim', en: 'Industrial manufacturing' }, project: 'p1' },
  { sector: { tr: 'Eğitim teknolojisi', en: 'Education technology' }, project: 'p4' },
  { sector: { tr: 'Otelcilik ve turizm', en: 'Hospitality and tourism' }, project: 'p5' },
  { sector: { tr: 'Fuar ve etkinlik', en: 'Trade fairs and events' }, project: 'p6' },
  { sector: { tr: 'Etkinlik biletleme', en: 'Event ticketing' }, project: 'p10' },
  { sector: { tr: 'İlan platformu', en: 'Listings marketplace' }, project: 'p8' },
  { sector: { tr: 'B2B gıda tedariği', en: 'B2B food supply' }, project: 'p9' },
];

export const ABOUT_PRINCIPLES_TITLE: L = { tr: 'Çalışma ilkelerimiz', en: 'Operating principles' };

export const ABOUT_PRINCIPLES: Array<{ title: L; description: L }> = [
  {
    title: { tr: 'Tek ekip, tek sorumluluk', en: 'One team, one point of accountability' },
    description: {
      tr: 'Strateji, tasarım ve mühendislik aynı ekipte yürür. Sonuçtan tek bir ekip sorumludur; ajans ve yazılım firması arasında kaybolan kararlar olmaz.',
      en: 'Strategy, design and engineering sit in the same team. One team owns the outcome, so no decision gets lost between an agency and a development shop.',
    },
  },
  {
    title: { tr: 'Önce kapsam, sonra kod', en: 'Scope before code' },
    description: {
      tr: 'Geliştirme, yazılı keşif dokümanı ve net kabul kriterleri üzerinde uzlaşıldıktan sonra başlar. Bütçe ve takvim bu dokümana dayanır.',
      en: 'Development starts only after a written discovery document and clear acceptance criteria are agreed. Budget and timeline are built on that document.',
    },
  },
  {
    title: { tr: 'Demo değil, production', en: 'Production, not demos' },
    description: {
      tr: 'Test, güvenlik, performans ve izlenebilirlik teslimatın parçasıdır. AI özellikleri de guardrail ve ölçüm ile canlıya çıkar.',
      en: 'Testing, security, performance and observability are part of the deliverable. AI features ship to production with guardrails and measurement.',
    },
  },
  {
    title: { tr: 'Açık ilerleme', en: 'Visible progress' },
    description: {
      tr: 'Düzenli demolar, paylaşılan yol haritası ve kayıt altına alınan teknik kararlar. Projenin durumunu her an bilirsiniz.',
      en: 'Regular demos, a shared roadmap and documented technical decisions. You always know where the project stands.',
    },
  },
  {
    title: { tr: 'Sözleşmeyle netleşen haklar', en: 'Rights defined in writing' },
    description: {
      tr: 'Kapsam, lisans ve fikri mülkiyet hakları imzalı teklif ve hizmet sözleşmesinde açıkça belirlenir. Talep halinde gizlilik sözleşmesi (NDA) imzalanır.',
      en: 'Scope, licensing and intellectual property are set out in the signed proposal and service agreement. A non-disclosure agreement is available on request.',
    },
  },
  {
    title: { tr: 'Lansman sonrası sahiplik', en: 'Ownership after launch' },
    description: {
      tr: 'Canlıya çıkış bitiş çizgisi değildir. Performans takibi, iyileştirme döngüleri ve bakım desteğiyle ürünün büyümesine eşlik ederiz.',
      en: 'Launch is not the finish line. We stay with the product through performance monitoring, improvement cycles and maintenance support.',
    },
  },
];

export const ABOUT_STACK_TITLE: L = { tr: 'Teknoloji yığını', en: 'Technology stack' };
export const ABOUT_STACK_INTRO: L = {
  tr: 'Ürünün ihtiyacına göre seçtiğimiz, production ortamında kendini kanıtlamış teknolojiler.',
  en: 'Production-proven technologies, selected to fit each product’s requirements.',
};
export const ABOUT_STACK_GROUPS: Array<{ label: L; items: string[] }> = [
  { label: { tr: 'Arayüz', en: 'Frontend' }, items: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS'] },
  { label: { tr: 'Backend ve veri', en: 'Backend and data' }, items: ['Node.js', 'GraphQL', 'Supabase', 'PostgreSQL'] },
  { label: { tr: 'AI', en: 'AI' }, items: ['OpenAI', 'Anthropic Claude', 'RAG', 'AI agent workflow'] },
  { label: { tr: 'Altyapı', en: 'Infrastructure' }, items: ['AWS', 'Docker', 'CI/CD'] },
  { label: { tr: 'Ödeme', en: 'Payments' }, items: ['Stripe', 'iyzico', 'PayTR', 'Paddle'] },
  { label: { tr: 'Tasarım', en: 'Design' }, items: ['Figma', 'Design system', 'Prototipleme'] },
];

export const ABOUT_TEAM_TITLE: L = { tr: 'Ekip ve uzmanlık', en: 'Team and expertise' };
export const ABOUT_TEAM_ROLES_TITLE: L = { tr: 'Tam zamanlı ekip', en: 'Full-time team' };

export const ABOUT_PROFILES_TITLE: L = { tr: 'Doğrulanmış profiller', en: 'Verified profiles' };

export const ABOUT_CTA = {
  title: { tr: 'Projenizi birlikte değerlendirelim.', en: 'Let’s evaluate your project together.' },
  desc: {
    tr: 'Hedeflerinizi ve mevcut durumunuzu paylaşın. Bir iş günü içinde ürün ve mühendislik ekibimizden dönüş alırsınız.',
    en: 'Share your goals and current setup. Our product and engineering team will respond within one business day.',
  },
  primary: { tr: 'Görüşme talep edin', en: 'Request a consultation' },
  secondary: { tr: 'Projeleri inceleyin', en: 'View our work' },
} satisfies Record<string, L>;

export const ABOUT_FAQ: ServiceFaqItem[] = [
  {
    question: { tr: 'Figtures nedir?', en: 'What is Figtures?' },
    answer: {
      tr: `${FIGTURES_BRAND_TAGLINE.tr} ${FIGTURES_FOUNDING_YEAR} yılında kurulmuştur; strateji, UI/UX tasarım ve yazılım geliştirme aynı ekipte yürür.`,
      en: `${FIGTURES_BRAND_TAGLINE.en} Founded in ${FIGTURES_FOUNDING_YEAR}, it keeps strategy, UI/UX design and software engineering in the same team.`,
    },
  },
  {
    question: { tr: 'Figtures nerede bulunuyor?', en: 'Where is Figtures located?' },
    answer: {
      tr: 'Merkez ofisimiz İçerenköy Mahallesi, Ataşehir, İstanbul’dadır. İstanbul dışındaki ve yurt dışındaki müşterilerle uzaktan veya hibrit modelde çalışıyoruz.',
      en: 'Our headquarters is in İçerenköy, Ataşehir, Istanbul. We work with clients outside Istanbul and abroad in a remote or hybrid model.',
    },
  },
  {
    question: { tr: 'Figtures hangi hizmetleri sunuyor?', en: 'Which services does Figtures offer?' },
    answer: {
      tr: 'AI geliştirme, özel yazılım geliştirme, SaaS ve MVP geliştirme, web geliştirme, mobil uygulama geliştirme, UI/UX tasarım, AI otomasyon ve agent çözümleri ile startup ürün mühendisliği.',
      en: 'AI development, custom software development, SaaS and MVP development, web development, mobile app development, UI/UX design, AI automation and agents, and startup product engineering.',
    },
  },
  {
    question: { tr: 'Hangi sektörlerde proje teslim ettiniz?', en: 'Which industries have you delivered for?' },
    answer: {
      tr: 'Kurumsal portal (ailem · Aselsan), perakende (LC Waikiki), AI destekli moda SaaS (Outfica), endüstriyel üretim (3D Yanında), eğitim teknolojisi (Ztech), otelcilik (Royal Seginus), fuarcılık (Kolektif Fuarcılık), etkinlik biletleme (Backdoor Tickets), ilan platformu (evarkadaşı) ve B2B gıda tedariği (Bool).',
      en: 'Enterprise portals (ailem · Aselsan), retail (LC Waikiki), AI-powered fashion SaaS (Outfica), industrial manufacturing (3D Yanında), education technology (Ztech), hospitality (Royal Seginus), trade fairs (Kolektif Fuarcılık), event ticketing (Backdoor Tickets), listings (evarkadaşı) and B2B food supply (Bool).',
    },
  },
  {
    question: { tr: 'Bir proje nasıl başlıyor?', en: 'How does an engagement start?' },
    answer: {
      tr: 'İletişim formu veya e-posta ile talebinizi iletirsiniz. Bir iş günü içinde dönüş yapar, keşif görüşmesi planlarız. Görüşmenin ardından kapsam, takvim ve bütçeyi içeren yazılı teklif ve yol haritası sunarız.',
      en: 'You send your request through the contact form or by email. We respond within one business day and schedule a discovery call. After the call, we deliver a written proposal and roadmap covering scope, timeline and budget.',
    },
  },
  {
    question: { tr: 'Yurt dışındaki şirketlerle çalışıyor musunuz?', en: 'Do you work with companies outside Turkey?' },
    answer: {
      tr: 'Evet. Türkçe ve İngilizce çalışıyor, Avrupa ve global pazardaki müşterilere uzaktan teslimat yapıyoruz.',
      en: 'Yes. We work in Turkish and English and deliver remotely to clients in Europe and global markets.',
    },
  },
  {
    question: {
      tr: 'Figtures; Figura AI, Figr veya Figma ile bağlantılı mı?',
      en: 'Is Figtures related to Figura AI, Figr or Figma?',
    },
    answer: {
      tr: 'Hayır. Figtures, figtures.com adresinde faaliyet gösteren bağımsız bir product studio’dur. Figura AI, Figr, Figma veya benzer isimli tasarım araçlarıyla bir bağlantısı yoktur.',
      en: 'No. Figtures is an independent product studio operating at figtures.com. It is not affiliated with Figura AI, Figr, Figma or similarly named design tools.',
    },
  },
];

export function localizeAbout(item: L, lang: Locale): string {
  return item[lang];
}
